const {
  policy,
  normalizeRole,
  shouldUseGlobalRole,
  resolveEffectiveRole,
  getCapabilitiesForRoleStatus,
  hasCapabilities,
  getRouteRule,
  matchRouteRule,
} = require('../api/matchPermissionService');

const DEFAULT_FALLBACK_PATH = '/home';
const DEFAULT_MESSAGE_TEXT = '请向管理员申请访问权限';

const ADMIN_CAPABILITY_KEYS = Object.freeze({
  manageUser: 'canManageUser',
  manageBans: 'canManageBans',
  manageCleanup: 'canManageCleanup',
  managePlayers: 'canManagePlayers',
  managePoints: 'canManagePoints',
});

const PAGE_ACCESS_RULES = Object.freeze({
  homePage: Object.freeze({
    key: 'homePage',
    path: '/home',
    accessType: 'public',
  }),
  userPage: Object.freeze({
    key: 'userPage',
    path: '/user',
    accessType: 'public',
  }),
  matchesListPage: Object.freeze({
    key: 'matchesListPage',
    path: '/matchList',
    accessType: 'public',
  }),
  matchesPage: Object.freeze({
    key: 'matchesPage',
    path: '/matches/:id',
    accessType: 'public',
  }),
  liveAdjustHubPage: Object.freeze({
    key: 'liveAdjustHubPage',
    path: '/live-adjust/:matchID',
    accessType: 'capability',
    requiredCapabilities: ['match.liveAdjust'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  matchDetailsPage: Object.freeze({
    key: 'matchDetailsPage',
    path: '/matchDetails/:id',
    accessType: 'route',
    routeKey: 'matchDetails',
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  scheduleMatchPage: Object.freeze({
    key: 'scheduleMatchPage',
    path: '/schedule/:id',
    accessType: 'capability',
    requiredCapabilities: ['match.schedule.manage'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  recordPage: Object.freeze({
    key: 'recordPage',
    path: '/record/:id',
    accessType: 'role',
    allowedRoles: ['admin', 'referee', 'referee_admin'],
    matchScopedRoles: true,
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: '你没有权限访问该页面',
  }),
  manageUserPage: Object.freeze({
    key: 'manageUserPage',
    path: '/manageUser',
    accessType: 'adminCapability',
    adminCapability: ADMIN_CAPABILITY_KEYS.manageUser,
  }),
  manageBansPage: Object.freeze({
    key: 'manageBansPage',
    path: '/manageBans',
    accessType: 'role',
    allowedRoles: ['admin'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  managePlayersPage: Object.freeze({
    key: 'managePlayersPage',
    path: '/managePlayers',
    accessType: 'role',
    allowedRoles: ['admin'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: '只有管理员可以访问运动员管理',
  }),
  pointsManagePage: Object.freeze({
    key: 'pointsManagePage',
    path: '/managePoints',
    accessType: 'role',
    allowedRoles: ['admin'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: '只有全局管理员可以访问积分管理',
  }),
  drawPage: Object.freeze({
    key: 'drawPage',
    path: '/draw/:id',
    accessType: 'route',
    routeKey: 'drawPage',
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  drawAnySourcePage: Object.freeze({
    key: 'drawAnySourcePage',
    path: '/drawAnySource',
    accessType: 'role',
    allowedRoles: ['admin', 'scheduler'],
    allowAnyMatchRole: true,
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  drawDistributionPage: Object.freeze({
    key: 'drawDistributionPage',
    path: '/draw/:matchID/distribution',
    accessType: 'capability',
    requiredCapabilities: ['match.draw.manage'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  knockoutPage: Object.freeze({
    key: 'knockoutPage',
    path: '/knockout/:matchID/:groupID/:eventCode',
    accessType: 'route',
    routeKey: 'matchDetails',
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  groupOverviewPage: Object.freeze({
    key: 'groupOverviewPage',
    path: '/groupOverview/:matchID/:groupID/:eventCode',
    accessType: 'route',
    routeKey: 'matchDetails',
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  nowPlayingPageV2: Object.freeze({
    key: 'nowPlayingPageV2',
    path: '/match/:matchID/now-playing-v2',
    accessType: 'capability',
    requiredCapabilities: ['match.monitor.screens'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  nowPlayingPageForOne: Object.freeze({
    key: 'nowPlayingPageForOne',
    path: '/match/nowplaying_one/:matchID',
    accessType: 'capability',
    requiredCapabilities: ['match.monitor.screens'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  nowPlayingPageForStreaming: Object.freeze({
    key: 'nowPlayingPageForStreaming',
    path: '/match/nowplaying_streaming/:matchID',
    accessType: 'capability',
    requiredCapabilities: ['match.monitor.screens'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
  liveReorderPage: Object.freeze({
    key: 'liveReorderPage',
    path: '/live-adjust/:matchID/reorder',
    accessType: 'capability',
    requiredCapabilities: ['match.liveAdjust'],
    fallbackPath: DEFAULT_FALLBACK_PATH,
    messageText: DEFAULT_MESSAGE_TEXT,
  }),
});

const APP_ROUTE_PAGE_KEYS = Object.freeze([
  'homePage',
  'userPage',
  'matchesListPage',
  'matchesPage',
  'liveAdjustHubPage',
  'matchDetailsPage',
  'scheduleMatchPage',
  'recordPage',
  'manageUserPage',
  'manageBansPage',
  'managePlayersPage',
  'pointsManagePage',
  'drawPage',
  'drawAnySourcePage',
  'drawDistributionPage',
  'knockoutPage',
  'groupOverviewPage',
  'nowPlayingPageV2',
  'nowPlayingPageForOne',
  'nowPlayingPageForStreaming',
  'liveReorderPage',
]);

const INTERNAL_ACCESS_GUARD_PAGE_KEYS = Object.freeze([
  'liveAdjustHubPage',
  'manageBansPage',
  'managePlayersPage',
  'pointsManagePage',
  'nowPlayingPageV2',
  'nowPlayingPageForOne',
  'nowPlayingPageForStreaming',
  'liveReorderPage',
]);

const MATCH_STATUS_DEPENDENT_PAGE_KEYS = Object.freeze(
  Object.values(PAGE_ACCESS_RULES)
    .filter((rule) => rule.accessType === 'capability' || rule.accessType === 'route')
    .map((rule) => rule.key)
);

const MATCH_STATUSES = Object.freeze([...(Array.isArray(policy.statuses) ? policy.statuses : [])]);

function getPageAccessRule(pageKeyOrRule) {
  if (!pageKeyOrRule) return null;
  if (typeof pageKeyOrRule === 'string') {
    return PAGE_ACCESS_RULES[pageKeyOrRule] || null;
  }
  if (typeof pageKeyOrRule === 'object' && typeof pageKeyOrRule.key === 'string') {
    return pageKeyOrRule;
  }
  return null;
}

function cloneStringArray(values) {
  return (Array.isArray(values) ? values : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function toAccessGuardProps(pageKeyOrRule) {
  const rule = getPageAccessRule(pageKeyOrRule);
  if (!rule) return {};

  const props = {};

  if (rule.accessType === 'role') {
    props.allowedRoles = cloneStringArray(rule.allowedRoles);
    if (rule.allowAnyMatchRole) {
      props.allowAnyMatchRole = true;
    }
  }

  if (rule.accessType === 'capability') {
    props.requiredCapabilities = cloneStringArray(rule.requiredCapabilities);
    if (rule.requireAllCapabilities) {
      props.requireAllCapabilities = true;
    }
  }

  if (rule.accessType === 'route') {
    props.routeKey = String(rule.routeKey || '').trim();
  }

  if (rule.fallbackPath) {
    props.fallbackPath = rule.fallbackPath;
  }

  if (rule.messageText) {
    props.messageText = rule.messageText;
  }

  return props;
}

function normalizeMatchRoleList(matchRoles) {
  return cloneStringArray(matchRoles).map((role) => normalizeRole(role)).filter(Boolean);
}

function personaHasAllowedMatchRole(persona, allowedRoles) {
  return normalizeMatchRoleList(persona?.matchRoles).some((role) => allowedRoles.includes(role));
}

function resolvePersonaCapabilityContext(persona, status) {
  const resolved = resolveEffectiveRole({
    globalRole: persona?.globalRole || '',
    matchRoles: normalizeMatchRoleList(persona?.matchRoles),
  });
  const normalizedStatus = String(status || MATCH_STATUSES[0] || '可报名').trim();
  const capabilitySet = getCapabilitiesForRoleStatus(resolved?.resolvedRole || 'user', normalizedStatus);
  return {
    ...resolved,
    matchStatus: normalizedStatus,
    capabilitySet,
  };
}

function isAdminCapabilityGranted(persona, adminCapability) {
  const resolution = resolveEffectiveRole({
    globalRole: persona?.globalRole || '',
    matchRoles: normalizeMatchRoleList(persona?.matchRoles),
  });
  const isAdmin = String(resolution?.resolvedRole || '') === 'admin';
  const isGlobalAdmin = isAdmin && String(resolution?.source || '') === 'global';
  switch (String(adminCapability || '').trim()) {
    case ADMIN_CAPABILITY_KEYS.manageUser:
      return isAdmin;
    case ADMIN_CAPABILITY_KEYS.manageBans:
    case ADMIN_CAPABILITY_KEYS.manageCleanup:
    case ADMIN_CAPABILITY_KEYS.managePlayers:
    case ADMIN_CAPABILITY_KEYS.managePoints:
      return isGlobalAdmin;
    default:
      return false;
  }
}

function isPageAccessibleForPersona(pageKeyOrRule, persona, status) {
  const rule = getPageAccessRule(pageKeyOrRule);
  if (!rule) return false;

  switch (rule.accessType) {
    case 'public':
      return true;
    case 'role': {
      const allowedRoles = cloneStringArray(rule.allowedRoles).map((role) => normalizeRole(role));
      const globalRole = normalizeRole(persona?.globalRole || '');
      if (shouldUseGlobalRole(globalRole)) {
        return allowedRoles.includes(globalRole);
      }
      if (rule.allowAnyMatchRole) {
        return personaHasAllowedMatchRole(persona, allowedRoles);
      }
      if (rule.matchScopedRoles) {
        return personaHasAllowedMatchRole(persona, allowedRoles);
      }
      return false;
    }
    case 'adminCapability':
      return isAdminCapabilityGranted(persona, rule.adminCapability);
    case 'capability': {
      const context = resolvePersonaCapabilityContext(persona, status);
      return hasCapabilities(
        context.capabilitySet,
        cloneStringArray(rule.requiredCapabilities),
        Boolean(rule.requireAllCapabilities)
      );
    }
    case 'route': {
      const routeRule = getRouteRule(rule.routeKey);
      const context = resolvePersonaCapabilityContext(persona, status);
      return matchRouteRule(context.capabilitySet, routeRule);
    }
    default:
      return false;
  }
}

function isPageAccessibleForPermissionContext(pageKeyOrRule, permissionContext) {
  const context = permissionContext && typeof permissionContext === 'object'
    ? permissionContext
    : {};
  const persona = {
    globalRole: context.globalRole || (context.source === 'global' ? context.resolvedRole || '' : ''),
    matchRoles: Array.isArray(context.matchRoles) && context.matchRoles.length > 0
      ? context.matchRoles
      : (context.source === 'match' && context.resolvedRole ? [context.resolvedRole] : []),
  };
  return isPageAccessibleForPersona(pageKeyOrRule, persona, context.matchStatus);
}

module.exports = {
  ADMIN_CAPABILITY_KEYS,
  PAGE_ACCESS_RULES,
  APP_ROUTE_PAGE_KEYS,
  INTERNAL_ACCESS_GUARD_PAGE_KEYS,
  MATCH_STATUS_DEPENDENT_PAGE_KEYS,
  MATCH_STATUSES,
  getPageAccessRule,
  toAccessGuardProps,
  resolvePersonaCapabilityContext,
  isAdminCapabilityGranted,
  isPageAccessibleForPersona,
  isPageAccessibleForPermissionContext,
};
